import { useState } from "react"
import { Formik, Field } from "formik"
import { PRIVACY_POLICY_URL, LOGO } from "../../constants/textConstants"
import MultiStepFlowLayout from "../../Components/MultiStepFlow/MultiStepFlowLayout"
import MultiStepFlowHeader from "../../Components/MultiStepFlow/MultiStepFlowHeader"
import MultiStepFlowFooter from "../../Components/MultiStepFlow/MultiStepFlowFooter"
import { useOutletContextPlan } from "../../hooks/useOutletContextPlan"
import { useOutletContextPlanActions } from "../../hooks/useOutletContextPlanActions"
import Spinner from "../../Components/Spinner"
import { useNavigate } from "react-router-dom"
import { PLAN_STATUS } from "../../constants/planStatus"
import LockedStep from "../../Components/LockedStep"
import { isStepLocked } from "../../lib/plan"
import { INVITE_STUDENT_RESOURCE_URL } from "../../constants/resources"
import { MoreInfoVideoModal } from "../../Components/MoreInfoVideoModal"
import { IconLink } from "../../Components/Link"
import TextField from "../../Components/Forms/FieldTypes/TextField"
import Button from "../../Components/Button"
import api from "../../services/api"

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const InviteStudent = () => {
  const { handleInviteStudent, handleEnterResponsesMyself, error, loading } =
    useOutletContextPlanActions()
  const { student, currentStep, plan, getPlan } = useOutletContextPlan()
  const navigate = useNavigate()
  const [selectedOption, setSelectedOption] = useState(1)

  const [savedEmail, setSavedEmail] = useState(student?.email ?? "")

  const handleSaveEmail = email => {
    api
      .createPlanContent({
        reset: true,
        content: [{ key: "student_email", value: email, plan_token: plan.token }],
      })
      .then(() => {
        setSavedEmail(email)
        getPlan()
      })
  }

  const handleDownloadGuardianLetter = () => {
    api.guardianLetter(plan.token).then(response => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "parent-guardian-information-sheet.pdf")
      document.body.appendChild(link)
      link.click()
      link.remove()
    })
  }

  if (isStepLocked(PLAN_STATUS.INVITE_STUDENT, currentStep)) {
    return <LockedStep />
  }

  return (
    <Formik
      initialValues={{
        studentEmail: student?.email ?? "",
        option1GuardianChecked: false,
        option2GuardianChecked: false,
      }}
      onSubmit={() => {}}
    >
      {({ values }) => (
        <>
          {loading && <Spinner overlay message="Just a moment..." />}
          <MultiStepFlowLayout
            header={
              <MultiStepFlowHeader
                onBack={() => navigate(`/plan/${plan?.token}/checklist`)}
                alwaysShowBackButton={true}
                logo={LOGO}
                currentStep={0}
                totalSteps={1}
                showProgress={false}
              />
            }
            footer={<MultiStepFlowFooter />}
            errors={error ? error.message : undefined}
          >
            <div className="mx-auto min-h-[60vh] max-w-md space-y-6 text-center">
              {/* Icon + Title */}
              <div className="space-y-2">
                <p className="mb-4 text-3xl font-medium italic text-brandSecondary">📧</p>
                <h2 className="text-2xl font-bold text-brandSecondary">
                  Invite Student!
                </h2>
              </div>

              {/* Subtitle */}
              <p className="text-base text-gray-700">
                Send your Student an email or choose to skip this step and capture the
                Student&apos;s responses yourself
              </p>

              {/* Resource links */}
              <div className="flex flex-col items-center gap-1">
                <div className="flex flex-col items-start gap-1">
                  <MoreInfoVideoModal
                    src={INVITE_STUDENT_RESOURCE_URL}
                    text=""
                    postText="How to invite a Student"
                    title="Inviting Your Student"
                    textClassName="text-brand no-underline"
                  />
                  <IconLink
                    icon="fa-solid fa-file-arrow-down"
                    onClick={handleDownloadGuardianLetter}
                  >
                    Download Parent Info Sheet
                  </IconLink>
                </div>
              </div>

              {/* Option 1 */}
              {selectedOption === 1 ? (
                <div className="rounded-2xl border-2 border-brandSecondary bg-white p-6 text-center">
                  <h3 className="mb-4 text-xl font-bold text-brandSecondary">
                    Option 1: Invite Student by Email
                  </h3>
                  <p className="mb-4 text-base text-gray-700">
                    The Student will receive an email invitation to create an account and access
                    their plan.
                  </p>

                  <div className="mx-auto mb-4 flex max-w-sm items-center gap-2">
                    <div className="flex-1">
                      <TextField
                        name="studentEmail"
                        type="email"
                        placeholder="student@email.com"
                        className="mt-0"
                      />
                    </div>
                    <div className="flex w-20 items-center justify-center">
                      {values.studentEmail === savedEmail && savedEmail ? (
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-green-700 text-center shadow">
                          <i className={"fa-solid fa-check text-white"}></i>
                        </span>
                      ) : (
                        <Button
                          variant="secondary-outline"
                          padding="px-4 py-2"
                          className="shrink-0"
                          type="button"
                          disabled={
                            !EMAIL_PATTERN.test(values.studentEmail) ||
                            values.studentEmail === savedEmail
                          }
                          onClick={() => handleSaveEmail(values.studentEmail)}
                        >
                          Save
                        </Button>
                      )}
                    </div>
                  </div>

                  <label className="mx-auto mb-4 flex max-w-sm cursor-pointer items-start gap-3 text-left">
                    <Field
                      type="checkbox"
                      name="option1GuardianChecked"
                      className="mt-1 h-4 w-4 rounded border-brandSecondary text-brandSecondary focus:ring-brandSecondary"
                    />
                    <span className="flex-1 text-sm text-gray-900">
                      I confirm the{" "}
                      <button
                        type="button"
                        onClick={handleDownloadGuardianLetter}
                        className="underline hover:text-brandSecondary"
                      >
                        Parent or Guardian has been informed
                      </button>
                    </span>
                  </label>

                  <div className="flex justify-center">
                    <Button
                      variant="secondary"
                      width="w-64"
                      onClick={() =>
                        handleInviteStudent({
                          firstName: student.first_name,
                          lastName: student.last_name,
                          email: values.studentEmail,
                        }).then(() => navigate("../student-invited"))
                      }
                      disabled={
                        !EMAIL_PATTERN.test(values.studentEmail) ||
                        !values.option1GuardianChecked ||
                        loading
                      }
                    >
                      Invite your student
                    </Button>
                  </div>

                  <p className="mt-3 text-center text-xs text-gray-400">
                    By inviting Student, you agree to our{" "}
                    <a href={PRIVACY_POLICY_URL} className="underline hover:text-gray-600">
                      Privacy Policy
                    </a>
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setSelectedOption(1)}
                    className="text-lg font-bold text-brandSecondary hover:opacity-80"
                  >
                    Option 1: Invite Student by Email
                  </button>
                </div>
              )}

              {/* Option 2 */}
              {selectedOption === 2 ? (
                <div className="rounded-2xl border-2 border-brandSecondary bg-white p-6 text-center">
                  <h3 className="mb-4 text-xl font-bold text-brandSecondary">
                    Option 2: Enter the Student&apos;s Responses Myself
                  </h3>
                  <p className="mb-4 text-base text-gray-700">
                    Choosing this option, you will be able to enter the responses yourself while
                    you sit with the student. However, the student will not be able to login and
                    make any changes.
                  </p>

                  <label className="mx-auto mb-4 flex max-w-sm cursor-pointer items-start gap-3 text-left">
                    <Field
                      type="checkbox"
                      name="option2GuardianChecked"
                      className="mt-1 h-4 w-4 rounded border-brandSecondary text-brandSecondary focus:ring-brandSecondary"
                    />
                    <span className="flex-1 text-sm text-gray-900">
                      I confirm the{" "}
                      <button
                        type="button"
                        onClick={handleDownloadGuardianLetter}
                        className="underline hover:text-brandSecondary"
                      >
                        Parent or Guardian has been informed
                      </button>
                    </span>
                  </label>

                  <div className="flex justify-center">
                    <Button
                      variant="secondary"
                      width="w-64"
                      onClick={() => handleEnterResponsesMyself().then(() => navigate("../"))}
                      disabled={!values.option2GuardianChecked || loading}
                    >
                      Enter Responses Myself
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setSelectedOption(2)}
                    className="text-lg font-bold text-brandSecondary hover:opacity-80"
                  >
                    Option 2: Enter the Student&apos;s Responses Myself
                  </button>
                </div>
              )}
            </div>
          </MultiStepFlowLayout>
        </>
      )}
    </Formik>
  )
}

export default InviteStudent
